import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { IConfiguration } from '../../../configuration/config.types';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { decodeImage } from '../../helpers/imageDecoder.helpers';
import { ACDLError, Failed, OperationResult, PreprocessingError, UploadSuccess } from '../../models/error.model';
import { IApiService } from '../../services/api.service.base';
import { IApiServiceImageUpload } from '../../services/api.service.imageUpload';
import { IBrowserService } from '../../services/browser.service';
import { ICryptoService } from '../../services/crypto.service';
import { IUploadsHubService } from '../../services/uploadsHub.service';
import { DatasetUploadCompletedWithInfoNotification, NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { generateThumbnail, stripExifTags } from './helpers/image.helpers';
import { IDatasetUpload, IDatasetUploadService, IUploadedImage, UploadServiceBase, WillImageUploadExceedsLimitResult } from './uploadServiceBase';

export interface IImageFile {
  file: File;
}

export interface IImagesUploaderService extends IDatasetUploadService<IImageFile> {
  getDuplicatedImageNamesAsync(datasetId: string, imageFileNames: string[]): Promise<string[]>;
  willExceedsImageLimit(workspaceId: string, newImagesCount: number, newImagesSizeInMB: number): Promise<WillImageUploadExceedsLimitResult>;
}

export namespace IImagesUploaderService {
  export const $: interfaces.ServiceIdentifier<IImagesUploaderService> = Symbol('IMAGES_UPLOADER_SERVICE');
}

@injectable()
export class ImagesUploadService extends UploadServiceBase<IImageFile> implements IImagesUploaderService {
  constructor(
    @inject(IBrowserService.$) browserService: IBrowserService,
    @inject(INotificationsService.$) notificationService: INotificationsService,
    @inject(IApiServiceImageUpload.$) apiServiceImageUpload: IApiService,
    @inject(IConfiguration.$) configuration: IConfiguration,
    @inject(IUploadsHubService.$) uploadsHub: IUploadsHubService,
    @inject(ICryptoService.$) cryptoService: ICryptoService,
    @inject(ICurrentWorkspaceStore.$) currentWorkspaceStore: ICurrentWorkspaceStore
  ) {
    super(browserService, notificationService, apiServiceImageUpload, configuration, uploadsHub, cryptoService, currentWorkspaceStore);

    makeObservable(this, {
      getDuplicatedImageNamesAsync: action,
      upload: action,
    });
  }

  async getDuplicatedImageNamesAsync(datasetId: string, imageFileNames: string[]): Promise<string[]> {
    const distinct = imageFileNames.filter((v, i, a) => a.indexOf(v) === i);

    const url = '/Datasets/getDuplicatedFileNames';
    const result = await this.apiServiceImageUpload.postAsync<string[]>(url, { datasetId, imageNames: distinct });

    if (result instanceof ACDLError) {
      throw result;
    }

    return result;
  }

  async upload(item: IImageFile, datasetId: string): Promise<OperationResult> {
    const url = '/datasets/addImage';
    const formData = new FormData();
    formData.append('datasetId', datasetId);
    formData.append('fileName', item.file.name);
    try {
      const encrypt = this.cryptoService.hasKey(this.currentWorkspaceStore.currentWorkspace.id);
      const strippedImage = await stripExifTags(item.file);
      const strippedImageBuffer = await strippedImage.arrayBuffer();

      const meta = decodeImage(strippedImageBuffer);
      formData.append('width', meta.width.toString());
      formData.append('height', meta.height.toString());

      formData.append('isEncrypted', encrypt ? 'true' : 'false');
      formData.append('image', encrypt ? await this.cryptoService.encrypt(this.currentWorkspaceStore.currentWorkspace.id, strippedImageBuffer) : strippedImage);
      if (encrypt) {
        const thumbnail = await generateThumbnail(strippedImage);
        formData.append('thumbnail', await this.cryptoService.encrypt(this.currentWorkspaceStore.currentWorkspace.id, await thumbnail.arrayBuffer()));
      }
    } catch (error: unknown) {
      return new Failed(new PreprocessingError(error as Error));
    }
    if (this.isDatasetUploadAborted(datasetId)) {
      return new Failed();
    }
    try {
      const result = await this.apiServiceImageUpload.postAsync<IUploadedImage>(url, formData);
      if (result instanceof ACDLError) {
        return new Failed(result);
      }
      return new UploadSuccess(result.imageSize);
    } catch {
      return new Failed();
    }
  }

  async handleUploadError(datasetId: string, result: Failed, item: IImageFile): Promise<void> {
    if (result && result.Error?.isBadRequestWithCode(['WRONG_FILE_TYPE'])) {
      await this.notificationService.push(
        new ToastNotification(NotificationLevel.WARNING, { template: 'file_x_is_not_an_image', data: { fileName: item.file.name } })
      );
      result.isHandled = true;
    } else {
      await super.handleUploadError(datasetId, result);
    }
  }

  async datasetUploadCompletedWithInfoNotification(dataset: IDatasetUpload<IImageFile>): Promise<void> {
    await this.notificationService.push(
      new DatasetUploadCompletedWithInfoNotification(
        dataset.datasetName,
        dataset.rejectedItems.map((it) => it.file.name)
      )
    );
  }

  async willExceedsImageLimit(workspaceId: string, newImagesCount: number, newImagesSizeInMB: number): Promise<WillImageUploadExceedsLimitResult> {
    const url = `/Datasets/WillImageUploadExceedsLimit?WorkspaceId=${workspaceId}&NewImagesCount=${newImagesCount}&NewImageSize=${newImagesSizeInMB}`;
    const result = await this.apiServiceImageUpload.getAsync<WillImageUploadExceedsLimitResult>(url);
    if (result instanceof ACDLError) {
      throw result;
    }

    return result;
  }
}
